import type {ContentPageEntry} from "./contentPages.ts";

/**
 * The route records a locale's tree appends for its markdown content pages (ui#1004).
 *
 * **Its own module, and the separation is measured rather than stylistic.** This is the ONE piece of
 * the content pipeline `src/routes.tsx` imports eagerly, and a module lands in the eager chunk whole:
 * leaving this beside `parseContentDocument` put the frontmatter and markdown parsers in `app.js` for
 * every visitor (+6.5 KB), even though only the lazily-loaded page module ever calls them. Everything
 * here imports TYPES only, so the eager cost of the whole pipeline is this function plus the generated
 * index — and neither grows with the corpus.
 *
 * React-free by construction (the `logic-outside-react` rule); the loader is injected by the route
 * table rather than imported.
 */

/** A content page's route record: tree-relative path, lazy module, error element, preload module. */
export interface ContentRouteRecord<M, E> {
	path: string;
	lazy: () => Promise<M>;
	entry: string;
	/**
	 * Rendered when `lazy` rejects — which a deploy makes routine, by rotating chunk hashes under an
	 * open tab. These are the first public lazy routes in the app, so before them a rejected import had
	 * nowhere to land but react-router's own default screen, which replaces the whole site.
	 */
	errorElement: E;
}

/**
 * The route records to append to a tree, in the order {@link ./contentTrees.ts} selected them.
 *
 * **Every record is `lazy` and none names a `Component`, and that is the load-bearing part.** The
 * other public routes are folded into the main bundle deliberately (see the note atop `routes.tsx`),
 * but a corpus that grows to ~20 pages per language cannot join them — an eager import would put every
 * page's prose in `app.js` for every visitor. Lazy is also safe for these pre-rendered pages:
 * vite-react-ssg resolves the `lazy` of every route matching the current URL BEFORE it calls
 * `hydrateRoot` (see `matchRoutes(...).filter(m => m.route.lazy)` in its client bootstrap), so the
 * chunk is already in hand at hydration and there is no fallback to flash.
 *
 * `path` is tree-relative (no leading slash) because these become CHILDREN of the `/` and `/ro` route
 * records. WHICH entries a tree gets is decided by {@link ./contentTrees.ts} — it depends on the host,
 * not only on the locale, so this takes the selection rather than a locale to filter by.
 */
export function contentRouteRecords<M, E>(
	entries: readonly ContentPageEntry[],
	entryModule: string,
	loadPage: (entry: ContentPageEntry) => Promise<M>,
	errorElement: E,
): Array<ContentRouteRecord<M, E>> {
	return entries.map((entry) => ({
		path: entry.path.replace(/^\//, ""),
		lazy: () => loadPage(entry),
		entry: entryModule,
		errorElement,
	}));
}
